import {useConfig} from '@aboutyou/config';
import {Sponsored} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_sponsored.types';
import {SponsoredContentSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {
  originClick,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk';
import {
  TrackBASMonitoring,
  TrackPromotions,
  useTrackOriginClick,
} from '@aboutyou/tracking';
import {
  BrandLogo,
  COLORS,
  desktopAndUpLinaria,
  extraLargeScreensLinaria,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  largeScreensLinaria,
  mediaDesktopAndUp,
  mediaMaxMBreakpoint,
  mediaTabletAndUp,
  ProductImage,
  SPACING,
  tabletAndDownLinaria,
  tabletAndUpLinaria,
  Text,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {STREAM_TOP_SPACING} from '../../backend-services/Header/constants';
import {
  Content,
  Header,
  HorizontalSponsoredPlacementContainer,
} from '../SponsoredPlacement/HorizontalSponsoredPlacementContainer';
import {hasEnoughSponsoredProducts} from '../SponsoredPlacement/utils/productThreshold';
import {useSponsoredBannerTrackingKey} from './utils/useSponsoredBannerTrackingKey';

// Headlines longer than this are assumed to need a second line on desktop.
// A character threshold instead of a DOM measurement keeps the font size
// stable from SSR on, avoiding a visible jump after hydration.
const MAX_SINGLE_LINE_HEADLINE_LENGTH = 34;

export const SponsoredBanner: React.FC<SponsoredContentSection> = ({
  color,
  headline,
  image,
  video,
  promotionTracking,
  sponsored,
  brand,
  targetUrl,
  productImages,
}) => {
  const {device} = useConfig();
  const trackOriginClick = useTrackOriginClick();
  const isLongHeadline = headline.length > MAX_SINGLE_LINE_HEADLINE_LENGTH;
  const trackPromotionKey = useSponsoredBannerTrackingKey(
    promotionTracking.promotionId,
  );
  const isMobile = device === 'mobile';
  const hasEnoughProducts = hasEnoughSponsoredProducts({
    placement: 'header',
    device: isMobile ? 'mobile' : 'desktop',
    count: productImages.length,
  });

  const stableVideo = trackPromotionKey ? video : undefined;
  const visibleProductImages = isMobile
    ? productImages.slice(0, 2)
    : productImages;

  if (!hasEnoughProducts) {
    return (
      <TrackBASMonitoring
        basPromotionSource={TrackingPromotionCreative.BasCategoryHeader}
        monitoringId="insufficient_products_category_header"
      />
    );
  }

  return (
    <TrackPromotions
      key={trackPromotionKey}
      disabled={!trackPromotionKey}
      promotion={{
        advertisement: {
          id: Number(promotionTracking.destination?.split('|')[1]),
          name: promotionTracking.promotionName,
          path: targetUrl.url,
          promotionId: promotionTracking.promotionId,
          locationId: promotionTracking.locationId,
          creativeName: promotionTracking.creativeName,
          creativeSlot: promotionTracking.creativeSlot,
        },
        source: TrackingPromotionCreative.BasCategoryHeader,
      }}
      streamPosition={0}
      threshold={0.5}
      triggerOnce
    >
      <StyledHorizontalSponsoredPlacementContainer
        contentAlignment="start"
        color={color}
        image={image}
        video={stableVideo}
        targetUrl={targetUrl.url}
        onClick={() => {
          trackOriginClick(promotionTracking.locationId as originClick)();
        }}
        promotionTracking={promotionTracking}
        brand={brand}
        sponsored={sponsored}
      >
        <Wrapper>
          <Headline
            $hasBrand={!!brand}
            color="White"
            data-long-headline={isLongHeadline}
          >
            {headline}
          </Headline>
          <ProductList>
            {visibleProductImages.map((image, index) => (
              <StyledProductImage
                key={`${image.url}-${index}`}
                src={image.url}
                alt={image.description}
                widths={[120, 240, 480]}
                breakpoints={[{columns: 5}]}
                padded
              />
            ))}
            <SeeMoreTile aria-hidden="true">
              <Icon
                asset={GenericIconName.RightArrowSharpEdges}
                size="S"
                fill={color.hex}
              />
            </SeeMoreTile>
          </ProductList>
        </Wrapper>
        {brand && (
          <StyledBrandLogo
            $hasImage={!!brand.deprecatedWhiteLogoImage}
            name={brand.name}
            image={brand.deprecatedWhiteLogoImage}
            maxHeight="40px"
            showTooltip={false}
          />
        )}
      </StyledHorizontalSponsoredPlacementContainer>
    </TrackPromotions>
  );
};

const StyledHorizontalSponsoredPlacementContainer = styled(
  HorizontalSponsoredPlacementContainer,
)<{sponsored?: Sponsored}>`
  aspect-ratio: 4 / 1.5;
  border-radius: 4px;

  @media ${mediaTabletAndUp} {
    aspect-ratio: 5 / 1;
    border-radius: 4px;
    margin-bottom: ${STREAM_TOP_SPACING}px;
  }

  ${Header} {
    --spacing: 0px;

    @media ${mediaTabletAndUp} {
      --spacing: 4px;
    }
  }

  ${Content} {
    top: 0;
    padding: 0;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 4px;
  width: min(500px, 50%);
  min-width: 0;
  padding: ${SPACING['Y.Md']};

  ${tabletAndDownLinaria(`
    gap: 0px;
  `)}

  ${desktopAndUpLinaria(`
    padding: ${SPACING['Y.Xl3']} ${SPACING['X.Xl3']} ${SPACING['Y.Lg']} ${SPACING['X.Lg']};
  `)}

  ${largeScreensLinaria(`
    gap: 24px;
  `)}
`;

const Headline = styled(Text)<{$hasBrand: boolean}>`
  position: relative;
  display: ${props => (props.$hasBrand ? 'none' : '-webkit-box')};
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.w500};
  line-height: 1.5;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  // The banner's aspect ratio can leave the flex column fractionally short of
  // two line boxes; a squeezed headline makes Chrome's line-clamp paint an
  // ellipsis after the first line even though both lines are visible.
  flex-shrink: 0;

  ${tabletAndUpLinaria(`
    display: -webkit-box;
  `)}

  ${desktopAndUpLinaria(`
    font-size: ${FONT_SIZES.px20};
    line-height: 1.2;
  `)}

  // Design rule: a headline that needs a second line steps down from H5 to H6.
  // Bounded media query as it applies to the desktop range only, not extra
  // large screens.
  @media ${mediaDesktopAndUp} and (max-width: ${mediaMaxMBreakpoint}px) {
    &[data-long-headline='true'] {
      font-size: ${FONT_SIZES.px16};
      line-height: 1.25;
    }
  }

  ${extraLargeScreensLinaria(`
    font-size: ${FONT_SIZES.px24};
  `)}
`;

const ProductList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  width: 100%;
  height: 60%;
  max-height: 120px;

  ${tabletAndUpLinaria(`
    grid-template-columns: repeat(6, 1fr);
  `)}
`;

const StyledProductImage = styled(ProductImage)`
  height: 100%;
  && {
    > img {
      max-width: 80%;
      max-height: 80%;
    }
  }
`;

const SeeMoreTile = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: ${COLORS.ProductImageBackground};
`;

const StyledBrandLogo = styled(BrandLogo)<{$hasImage: boolean}>`
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  width: auto;
  height: auto;
  z-index: 1;
  color: ${COLORS.White};
  font-size: ${FONT_SIZES.px18};
  font-weight: ${FONT_WEIGHTS.w700};
  max-width: ${props => (props.$hasImage ? '30%' : 'min-content')};

  ${tabletAndUpLinaria(`
    bottom: 16px;
    right: 16px;
    max-width: unset;
    height: 24px;
    font-size: ${FONT_SIZES.px24};
    `)}

  ${tabletAndDownLinaria(`
    img {
      max-height: 32px;
    }
  `)}


  ${desktopAndUpLinaria(`
    height: 40px;
    font-size: ${FONT_SIZES.px32};
  `)}
`;
